import { useEffect, useState } from 'react';

/**
 * use-viewport — EMSPCR Foundation v1.0 §7.3.
 *
 * Single source of truth for device mode across the platform. Per
 * §7.5 no feature is gated by device — every workflow must function
 * at every breakpoint — but component authors tune layout from the
 * returned mode.
 */

export type ViewportMode =
  | 'phone_portrait'    // < 640px — functional mobile, single-column + bottom tabs
  | 'phone_landscape'   // 640-767 — still mobile, wider cards
  | 'tablet_portrait'   // 768-1023 — primary iPad portrait
  | 'tablet_landscape'  // 1024-1365 — primary iPad landscape, two-panel capable
  | 'desktop'           // 1366-1919 — QA / admin / medical director
  | 'desktop_wide';     // ≥ 1920 — multi-column dashboard

export interface ViewportState {
  mode: ViewportMode;
  width: number;
  height: number;
  /** True at all breakpoints; kept for API symmetry with the spec. */
  functional: true;
  /** Coarse bucket used by many components. */
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  /** User-agent hint — iOS vs Android vs other. */
  platform: 'ios' | 'android' | 'other';
  /** Coarse pointer — true when the primary pointer is touch. */
  touch: boolean;
}

function detectMode(width: number): ViewportMode {
  if (width < 640)  return 'phone_portrait';
  if (width < 768)  return 'phone_landscape';
  if (width < 1024) return 'tablet_portrait';
  if (width < 1366) return 'tablet_landscape';
  if (width < 1920) return 'desktop';
  return 'desktop_wide';
}

function snapshot(): ViewportState {
  const w = typeof window === 'undefined' ? 1024 : window.innerWidth;
  const h = typeof window === 'undefined' ? 768  : window.innerHeight;
  const mode = detectMode(w);
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const platform: ViewportState['platform'] =
    /iphone|ipad|ipod/i.test(ua) ? 'ios'
      : /android/i.test(ua) ? 'android'
      : 'other';
  const touch = typeof window !== 'undefined'
    && (window.matchMedia?.('(pointer: coarse)').matches ?? false);
  return {
    mode,
    width: w,
    height: h,
    functional: true,
    isPhone: mode === 'phone_portrait' || mode === 'phone_landscape',
    isTablet: mode === 'tablet_portrait' || mode === 'tablet_landscape',
    isDesktop: mode === 'desktop' || mode === 'desktop_wide',
    platform,
    touch,
  };
}

/** Hook — re-renders when the viewport crosses a breakpoint or orientation changes. */
export function useViewport(): ViewportState {
  const [state, setState] = useState<ViewportState>(() => snapshot());
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let frame = 0;
    const onChange = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setState(snapshot()));
    };
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
      cancelAnimationFrame(frame);
    };
  }, []);
  return state;
}

/** Non-React reader — useful in validators, services, demo data. */
export function readViewport(): ViewportState {
  return snapshot();
}
